"use client";
// t() reads the page language, which the compiler cannot see, and this stays
// on screen while the language changes.
"use no memo";
import { t, useLocale } from "../i18n";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";
import WifiSlash from "~icons/ph/wifi-slash";
import { isRinging } from "../calls/call-state";
import { useCallSession } from "../calls/use-call-session";
import { useGroupCall } from "../group-calls/use-group-call";
import { Backdrop } from "../ui/components";
import { Outlet, useNavigate, useRouter } from "@tanstack/react-router";
import { screenPaths, useAppLocation, type Screen } from "./location";
import { AppNavigation } from "./navigation";
import { OpenDialog } from "./dialogs";
import { ShellContext, type Dialog, type Shell } from "./shell";
import { CallModal } from "../calls/call-modal";
import { MicrophoneCheck } from "../calls/microphone-check";
import { focusConversationSearch } from "../messages/messages-screen";
import type { CallMedia, Person } from "../../shared/types";
import { useQuery } from "@tanstack/react-query";
import { groupInvitationsOptions, profileLinkOptions } from "../data/queries";
import { useSessionCore } from "../data/session";
import { useAccountRecovery } from "../account/account-recovery";
import { useCallLinkActions } from "../links/call-link";
import { useCallNotifications } from "../notifications/use-call-notifications";
import { useAttention } from "../notifications/attention";
import { GroupInvitationNotice } from "../group-calls/group-invitation";
import { useMissedCalls } from "../notifications/missed-calls";
import { ReportDialog } from "../account/report-dialog";
import { notify, Toast } from "../ui/toast";

type PendingCall = { person: Person; media: CallMedia };
// It brings the group call library, which only a group call needs.
const GroupCallModal = lazy(() =>
  import("../group-calls/group-call-modal").then(({ GroupCallModal }) => ({
    default: GroupCallModal,
  })),
);

// The shell around every screen: the session, the one open dialog, and the
// live call. Screens are routes, and each flow keeps its own state.
export default function App() {
  const locale = useLocale();
  const { screen, chatId, inviteId, profileCode, signInCode, groupCode } =
      useAppLocation(),
    navigateTo = useNavigate(),
    router = useRouter();
  const [dialog, setDialog] = useState<Dialog | null>(null),
    [reportTarget, setReportTarget] = useState<Person | null>(null),
    [pendingCall, setPendingCall] = useState<PendingCall | null>(null),
    [callMinimized, setCallMinimized] = useState(false),
    [checkingMicrophone, setCheckingMicrophone] = useState(false),
    // Selecting Friends again starts that screen over.
    [friendsVisit, setFriendsVisit] = useState(0);
  const { snapshot: sessionQuery, connected } = useSessionCore();
  const snapshot = sessionQuery.data;
  const account = snapshot?.authenticated ? snapshot : null;
  const me = account?.me ?? null;
  const guest = snapshot?.authenticated === false ? snapshot.guest : null;
  const friends = account?.friends ?? [];
  const invitations = useQuery(groupInvitationsOptions(me?.id ?? null));
  const requests = account?.requests ?? [];
  const chat = friends.find((friend) => friend.id === chatId) ?? null;
  const ready = !!snapshot;
  // Without a session answer nobody can tell a visitor from a signed-in person,
  // so neither gets the visitor's page. It stays up while trying again.
  const unreachable =
    !snapshot &&
    (sessionQuery.errorUpdateCount > 0 ||
      sessionQuery.fetchStatus === "paused");
  const recovery = useAccountRecovery(account, friends.length > 0);
  const notifications = useCallNotifications(me?.id);
  const missedCalls = useMissedCalls(
    me?.id,
    account?.history ?? [],
    screen === "notifications" || screen === "history",
  );
  const {
    call: liveCall,
    ended: lastCall,
    session: callSession,
  } = useCallSession({
    notify,
    userId: me?.id ?? guest?.id,
    systemAlerts: notifications.on,
  });
  const canEnterGroup = useCallback(
    () => !callSession.getSnapshot().call,
    [callSession],
  );
  const groupCall = useGroupCall(canEnterGroup);
  const { createCallLink, cancelCallLink } = useCallLinkActions();
  // Guests have their own page for this.
  const endedCall = me ? lastCall : null;
  if (callMinimized && !liveCall && !endedCall) setCallMinimized(false);
  // A call needs the microphone, so it takes over from a check.
  if (checkingMicrophone && liveCall) setCheckingMicrophone(false);
  const covered =
    !!dialog || !!liveCall || checkingMicrophone || !!groupCall.live;
  useAttention(me?.id, friends, liveCall, notifications.on, chat?.id);
  const close = useCallback(() => setDialog(null), []);
  const openNotification = useEffectEvent(
    async (personId?: string, groupLink?: string) => {
      // Reuse call synchronization on the existing peer connection, and refresh
      // account data before revealing controls. The push payload is never state.
      const [snapshot] = await Promise.all([
        sessionQuery.refetch(),
        callSession.refresh(),
      ]);
      if (snapshot.isError) {
        notify("حدثت مشكلة في الاتصال. حاول مجددًا.");
        return;
      }
      close();
      if (groupLink) {
        void navigateTo({
          to: "/group/$groupCode",
          params: { groupCode: groupLink },
          search: true,
        });
        return;
      }
      // A message alert opens its chat, with any live call out of the way.
      const person =
        personId && snapshot.data?.authenticated
          ? snapshot.data.friends.find((friend) => friend.id === personId)
          : undefined;
      if (person) {
        showChat(person);
        setCheckingMicrophone(false);
      }
      setCallMinimized(!!person && !isRinging(liveCall));
    },
  );
  useEffect(() => {
    const open = (event: MessageEvent) => {
      if (event.data?.type === "notification-open")
        void openNotification(event.data.personId, event.data.groupLink);
    };
    navigator.serviceWorker?.addEventListener("message", open);
    return () => navigator.serviceWorker?.removeEventListener("message", open);
  }, []);
  const receiveCall = callSession.receive;
  useEffect(() => {
    if (snapshot?.authenticated) receiveCall(snapshot.active, snapshot.me.id);
    else if (snapshot?.guest)
      receiveCall(snapshot.guest.active, snapshot.guest.id);
  }, [snapshot, receiveCall]);
  // Joining waits for the signaling socket, which a new guest opens first.
  const settlePendingCall = useEffectEvent((call: PendingCall | null) => {
    setPendingCall(null);
    leaveInvite();
    if (call && !groupCall.live)
      void callSession.start(call.person, call.media);
    else notify("تعذر الاتصال. أعد تحميل الصفحة.");
  });
  useEffect(() => {
    if (!pendingCall) return;
    if (connected) {
      settlePendingCall(pendingCall);
      return;
    }
    const timer = setTimeout(() => settlePendingCall(null), 15000);
    return () => clearTimeout(timer);
  }, [pendingCall, connected]);
  // Going back or forward closes whatever was open over the page.
  const { clear: clearRecovery } = recovery;
  useEffect(
    () =>
      router.history.subscribe(({ action }) => {
        if (action.type === "PUSH" || action.type === "REPLACE") return;
        close();
        clearRecovery();
      }),
    [router, close, clearRecovery],
  );
  const signedIn = !!me;
  const openChatId = chat?.id;
  useEffect(() => {
    if (ready && chatId && !openChatId)
      void navigateTo({
        to: signedIn ? "/messages" : "/",
        search: true,
        replace: true,
      });
  }, [ready, chatId, openChatId, signedIn, navigateTo]);
  // Opening a chat moves focus on its own, inside its screen.
  const focusScreen = useEffectEvent(() => {
    if (!openChatId)
      document.getElementById("main-content")?.focus({ preventScroll: true });
  });
  const previousScreen = useRef(screen);
  useEffect(() => {
    if (screen !== "home" || previousScreen.current !== "home") focusScreen();
    previousScreen.current = screen;
  }, [screen, me?.id, friendsVisit]);
  // Another account starts on the home page, or on the link it opened.
  const resetForAccount = useEffectEvent(() => {
    if (!inviteId && !groupCode)
      void navigateTo({ to: "/", search: true, replace: true });
    close();
    recovery.clear();
    setCheckingMicrophone(false);
    void callSession.end(false);
    void groupCall.leave();
  });
  const previousUser = useRef(me?.id);
  useEffect(() => {
    if (previousUser.current && previousUser.current !== me?.id)
      resetForAccount();
    previousUser.current = me?.id;
  }, [me?.id]);
  useEffect(() => {
    if (sessionQuery.error && !unreachable)
      notify("تعذر الاتصال. أعد تحميل الصفحة.");
  }, [sessionQuery.error, unreachable]);
  // A profile link opens its owner's profile over the home page, or the
  // account's own link, then the address returns to the home page.
  const profileLink = useQuery({
    ...profileLinkOptions(me?.id ?? null, profileCode ?? ""),
    enabled: !sessionQuery.isPending && !!profileCode,
  });
  const linkedPerson = profileLink.data,
    linkError = profileLink.error;
  const openProfileLink = useEffectEvent(() => {
    if (!profileCode) return;
    if (me?.link_code === profileCode)
      setDialog({ kind: "add-friend", step: "share" });
    else if (linkedPerson)
      setDialog({ kind: "person", person: linkedPerson, code: profileCode });
    else if (linkError) notify(linkError.message);
    else return;
    void navigateTo({ to: "/", search: true, replace: true });
  });
  useEffect(() => {
    if (ready) openProfileLink();
  }, [ready, profileCode, linkedPerson, linkError]);
  // Back from a view never returns to a call link that was left.
  function leaveInvite() {
    if (inviteId) void navigateTo({ to: "/", search: true, replace: true });
  }
  function openScreen(view: Screen) {
    close();
    leaveInvite();
    if (view === screen && !chatId && !signInCode && !groupCode) {
      document.getElementById("main-content")?.focus();
      return;
    }
    void navigateTo({ to: screenPaths[view], search: true });
  }
  function navigate(view: Screen) {
    if (view === "messages" && screen === "messages" && !chat) {
      close();
      focusConversationSearch();
      return;
    }
    if (view === "friends" && screen === "friends")
      setFriendsVisit((n) => n + 1);
    openScreen(view);
  }
  function showChat(p: Person) {
    close();
    leaveInvite();
    // Switching chats replaces the open one, so Back leaves the conversation.
    void navigateTo({
      to: "/messages/$personId",
      params: { personId: p.id },
      search: true,
      replace: !!chatId,
    });
  }
  function startMicrophoneCheck() {
    if (liveCall || groupCall.live) return;
    callSession.dismissEnded();
    setCheckingMicrophone(true);
  }
  function callFriend(p: Person, media: CallMedia = "audio") {
    // One call at a time: bring the current call back instead.
    if (liveCall) {
      setCallMinimized(false);
      return;
    }
    if (!friends.some((friend) => friend.id === p.id)) {
      notify(t("أضف هذا الشخص إلى أصدقائك لبدء المحادثة"));
      return;
    }
    // Friends with call notifications on can be rung with no tab open.
    if (p.status !== "online" && !p.call_notifications) {
      notify(t("غير متاح حاليًا. يمكنك إرسال رسالة."));
      return;
    }
    if (groupCall.live) {
      notify("غادر المكالمة الجماعية أولًا لبدء مكالمة.");
      return;
    }
    void callSession.start(p, media);
  }
  if (unreachable)
    return (
      <div className="ranna-app">
        <Backdrop paused />
        <main className="search-home">
          <section className="search-hero invite-hero" role="alert">
            <div className="call-invite">
              <span className="call-invite-icon" aria-hidden="true">
                <WifiSlash width={28} height={28} />
              </span>
              <h1>{t("تعذر الوصول إلى رنّة")}</h1>
              <p className="call-invite-lead">
                {t("تحقق من اتصالك بالإنترنت وحاول مجددًا.")}
              </p>
              <button
                className="button secondary"
                aria-busy={sessionQuery.isFetching}
                onClick={() =>
                  void sessionQuery.refetch({ cancelRefetch: false })
                }
              >
                {t(
                  sessionQuery.isFetching
                    ? "جارٍ إعادة المحاولة…"
                    : "إعادة المحاولة",
                )}
              </button>
            </div>
          </section>
        </main>
      </div>
    );
  // Wait for the session before choosing the visitor or signed-in layout.
  if (!ready)
    return (
      <div className="ranna-app" aria-busy="true">
        <Backdrop paused />
      </div>
    );
  if (!snapshot.authenticated && snapshot.suspended)
    return (
      <div className="ranna-app">
        <main className="search-home">
          <section className="search-hero">
            <h1>{t("هذا الحساب موقوف مؤقتًا")}</h1>
            <p>
              {t(
                "يمكن للمشرف استعادة حسابك. احتفظ بهذه الجلسة ثم تحقق مجددًا، حتى إن لم يكن لديك مفتاح مرور أو رمز استعادة.",
              )}
            </p>
            <button
              className="button"
              onClick={() => void sessionQuery.refetch()}
            >
              {t("تحقق مجددًا")}
            </button>
          </section>
        </main>
      </div>
    );
  const shell: Shell = {
    dialog,
    open: setDialog,
    close,
    report: setReportTarget,
    covered,
    openScreen,
    showChat,
    // A call-link guest has a name and no profile.
    openPerson: (person) => {
      if (!person.guest) setDialog({ kind: "person", person });
    },
    chooseSafety: (person, action) => {
      if (action === "report") setReportTarget(person);
      else setDialog({ kind: "safety", person, action });
    },
    liveCall,
    session: callSession,
    groupCall,
    callFriend,
    joinCall: (person, media) => setPendingCall({ person, media }),
    joining: !!pendingCall,
    startMicrophoneCheck,
    connected,
    createCallLink,
    cancelCallLink,
    recovery,
  };
  return (
    <ShellContext value={shell}>
      <div
        className={`ranna-app ${me ? "signed-in" : ""} ${me && screen !== "home" ? "in-screen" : ""}`}
      >
        <Backdrop paused={covered || screen !== "home"} />
        <a
          className="skip-link"
          href="#main-content"
          onClick={(event) => {
            event.preventDefault();
            document.getElementById("main-content")?.focus();
          }}
        >
          {t("تجاوز إلى المحتوى")}
        </a>
        {me && (
          <AppNavigation
            key={locale}
            person={me}
            requests={requests.filter((p) => p.requested_by !== me.id).length}
            unread={friends.filter((p) => p.unread).length}
            notifications={missedCalls + (invitations.data?.length ?? 0)}
            active={screen}
            inert={
              !!dialog ||
              checkingMicrophone ||
              !!groupCall.live ||
              (!!liveCall && !callMinimized)
            }
            onNavigate={navigate}
          />
        )}
        <Outlet key={friendsVisit} />
        <GroupInvitationNotice invitations={invitations.data ?? []} />
        <Toast />
        {dialog && <OpenDialog dialog={dialog} notifications={notifications} />}
        {checkingMicrophone && (
          <MicrophoneCheck onClose={() => setCheckingMicrophone(false)} />
        )}
        {groupCall.live && (
          <Suspense>
            <GroupCallModal call={groupCall.live} onLeave={groupCall.leave} />
          </Suspense>
        )}
        <CallModal
          beforeAnswer={groupCall.live ? groupCall.leave : undefined}
          session={callSession}
          call={liveCall}
          ended={endedCall}
          friend={friends.find(
            (friend) => friend.id === (liveCall ?? endedCall)?.person.id,
          )}
          minimized={callMinimized}
          onMinimize={() => setCallMinimized(true)}
          onExpand={() => setCallMinimized(false)}
          onCallAgain={(person, media) => {
            callSession.dismissEnded();
            callFriend(person, media);
          }}
          onMessage={(person) => {
            callSession.dismissEnded();
            showChat(person);
          }}
        />
        {reportTarget && (
          <ReportDialog
            key={reportTarget.id}
            person={reportTarget}
            canBlock={
              !!me &&
              !reportTarget.guest &&
              !account?.blocked.some((person) => person.id === reportTarget.id)
            }
            onClose={() => setReportTarget(null)}
          />
        )}
      </div>
    </ShellContext>
  );
}
